import React, { useState } from 'react'
import clsx from 'clsx'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/utility/react-modal'
import { addTrackingNumber, removeTrackingNumber } from './registered_mail_tracking_number_api'

const uspsTrackingUrl = (trackingNumber) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`

function RemoveTrackingNumberModal({ packageHashedId, roundNumber, trackingNumber, onClose, onRemoved }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleRemove = async () => {
    setLoading(true)
    setError(null)
    try {
      await removeTrackingNumber(packageHashedId, roundNumber)
      onRemoved()
    } catch {
      setError('Something went wrong. Please try again.')
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalHeader>
        <span className="dome-h2 dome-color-dark-green">remove tracking number?</span>
      </ModalHeader>

      <ModalBody className="dome-gap12">
        <span className="dome-p1 dome-color-dark-grey">
          This will remove {trackingNumber} from this round. You can add a corrected tracking number afterward.
        </span>
        {error && <span className="dome-p3 dome-color-red-alert">{error}</span>}
      </ModalBody>

      <ModalFooter>
        <button type="button" onClick={onClose} disabled={loading} className="dome-btn dome-btn-link dome-btn-link-med-grey">
          cancel
        </button>
        <button type="button" onClick={handleRemove} disabled={loading} className="dome-btn dome-btn-base dome-btn-red-stroke">
          {loading ? 'removing…' : 'remove'}
        </button>
      </ModalFooter>
    </Modal>
  )
}

function AddTrackingNumberModal({ packageHashedId, roundNumber, onClose, onAdded }) {
  const [value, setValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const trimmed = value.trim()

  const handleSave = async () => {
    if (!trimmed || loading) return
    setLoading(true)
    setError(null)
    try {
      const { data } = await addTrackingNumber(packageHashedId, roundNumber, trimmed)
      onAdded(data.tracking_number)
    } catch {
      setError('Something went wrong. Please try again.')
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={true} onClose={onClose} autoFocus>
      <ModalHeader>
        <span className="dome-h2 dome-color-dark-green">add tracking number</span>
      </ModalHeader>

      <ModalBody className="dome-gap12">
        <label className="dome-d-flex dome-flex-column dome-gap6 dome-p2 dome-color-dark-grey">
          tracking number
          <input
            type="text"
            className="dome-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            autoFocus
          />
        </label>
        {error && <span className="dome-p3 dome-color-red-alert">{error}</span>}
      </ModalBody>

      <ModalFooter>
        <button type="button" onClick={onClose} disabled={loading} className="dome-btn dome-btn-link dome-btn-link-med-grey">
          cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!trimmed || loading}
          className={clsx('dome-btn dome-btn-base', trimmed ? 'dome-btn-dark-green' : 'dome-btn-disabled')}
        >
          {loading ? 'saving…' : 'save'}
        </button>
      </ModalFooter>
    </Modal>
  )
}

export default function RegisteredMailTrackingNumber({ packageHashedId, roundNumber, trackingNumber: initialTrackingNumber }) {
  const [trackingNumber, setTrackingNumber] = useState(initialTrackingNumber || null)
  const [modal, setModal] = useState(null) // null | 'remove' | 'add'

  return (
    <span className="registered-mail-tracking-number dome-d-flex dome-align-center dome-gap6">
      {trackingNumber ? (
        <>
          <a href={uspsTrackingUrl(trackingNumber)} target="_blank" rel="noopener noreferrer">
            {trackingNumber}
          </a>
          <i
            className="fal fa-xmark dome-color-red-alert dome-cursor-pointer"
            role="button"
            tabIndex={0}
            aria-label="remove tracking number"
            onClick={() => setModal('remove')}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setModal('remove')}
          />
        </>
      ) : (
        <button
          type="button"
          className="dome-btn dome-btn-base bp-status-item__add-tracking-number dome-btn-grey-stroke"
          onClick={() => setModal('add')}
        >
          add tracking number
        </button>
      )}

      {modal === 'remove' && (
        <RemoveTrackingNumberModal
          packageHashedId={packageHashedId}
          roundNumber={roundNumber}
          trackingNumber={trackingNumber}
          onClose={() => setModal(null)}
          onRemoved={() => {
            setTrackingNumber(null)
            setModal(null)
          }}
        />
      )}

      {modal === 'add' && (
        <AddTrackingNumberModal
          packageHashedId={packageHashedId}
          roundNumber={roundNumber}
          onClose={() => setModal(null)}
          onAdded={(newTrackingNumber) => {
            setTrackingNumber(newTrackingNumber)
            setModal(null)
          }}
        />
      )}
    </span>
  )
}
